import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { type IconProps, type NewCoreIconProps } from '@atlaskit/icon';
import { type BackgroundColor, Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import { AdControls, type Props as AdControlsProps } from '@post-office/components--ad-controls';
import { Avatar as GrowthAvatar } from '@post-office/components--avatar';
import { Avatar as PostOfficeAvatar } from '@post-office/components--in-app';
import { type ComponentType } from 'react';

import { CloseButton } from './ui/close-button';
import { type JSX } from 'react/jsx-runtime';

const styles = cssMap({
	root: {
		height: '100%',
		display: 'flex',
		alignItems: 'flex-end',
	},
	flag: {
		boxShadow: token('elevation.shadow.overlay'),
		borderRadius: token('radius.small'),
		zIndex: 600, // flag
		transition: 'background-color 200ms',
		backgroundColor: token('elevation.surface.overlay'),
		padding: token('space.200'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
	},
	avatar: {
		paddingRight: token('space.200'),
		paddingTop: token('space.050'),
	},
	headingSection: {
		flex: 1,
		paddingRight: token('space.050'),
		paddingTop: token('space.050'),
	},
	adControls: {
		gap: token('space.050'),
		paddingLeft: token('space.075'),
		marginLeft: 'auto',
	},
	children: {
		width: '290px',
	},
});

type BaseFlagProps = {
	width: number;
	onClose: () => void;
	onNotInterested: () => void;
	avatarSrc?: string;
	avatarBadge?: {
		icon: ComponentType<IconProps | NewCoreIconProps>;
		color: BackgroundColor;
		label: string;
	};
	avatarLineStartElementId?: string;
	avatarLineEndElementId?: string;
	headingSection?: React.ReactNode;
	bottomLayer?: React.ReactNode;
	children?: React.ReactNode;
	joiningLineHeight?: number;
};

type ShowWhyAmISeeingThisProps = {
	showWhyAmISeeingThis: true;
	whyAmISeeingThisContent: AdControlsProps['whyAmISeeingThisModalContent'];
};

type HideWhyAmISeeingThisProps = {
	showWhyAmISeeingThis: false;
	whyAmISeeingThisContent?: never;
};

export type FlagProps = BaseFlagProps & (ShowWhyAmISeeingThisProps | HideWhyAmISeeingThisProps);
export type AvatarProps = Pick<
	FlagProps,
	| 'avatarSrc'
	| 'avatarBadge'
	| 'avatarLineStartElementId'
	| 'avatarLineEndElementId'
	| 'joiningLineHeight'
>;

// Temporary Avatar component to manage showing Growth Avatar component or Post Office platformised Avatar component
const Avatar = ({
	avatarSrc,
	avatarBadge,
	avatarLineStartElementId,
	avatarLineEndElementId,
	joiningLineHeight,
}: AvatarProps) => {
	const isPlatformisedComponentEnabled = FeatureGates.checkGate(
		'post-office_enable_platformised-components-ccrn-ia',
	);
	return isPlatformisedComponentEnabled && avatarBadge ? (
		<PostOfficeAvatar
			src={avatarSrc}
			badgeIcon={{
				icon: avatarBadge.icon as ComponentType<NewCoreIconProps>,
				color: avatarBadge.color,
				label: avatarBadge.label,
			}}
		/>
	) : (
		<GrowthAvatar
			src={avatarSrc}
			badgeIcon={avatarBadge}
			avatarLineStartElementId={avatarLineStartElementId}
			avatarLineEndElementId={avatarLineEndElementId}
			joiningLineHeight={joiningLineHeight}
		/>
	);
};

export const Flag = ({
	width,
	onClose,
	onNotInterested,
	whyAmISeeingThisContent,
	avatarSrc,
	avatarBadge,
	avatarLineStartElementId,
	avatarLineEndElementId,
	headingSection,
	bottomLayer,
	children,
	joiningLineHeight,
}: FlagProps): JSX.Element => {
	return (
		<Box xcss={styles.root}>
			<Box
				xcss={styles.flag}
				style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
			>
				<Flex>
					<Box xcss={styles.avatar}>
						{avatarSrc && (
							<Avatar
								avatarSrc={avatarSrc}
								avatarBadge={avatarBadge}
								avatarLineStartElementId={avatarLineStartElementId}
								avatarLineEndElementId={avatarLineEndElementId}
								joiningLineHeight={joiningLineHeight}
							/>
						)}
					</Box>
					<Stack space="space.100" xcss={styles.headingSection}>
						<Flex>
							{headingSection}
							<Inline xcss={styles.adControls}>
								<AdControls
									zIndex={layers.flag()}
									onDismissed={onNotInterested}
									showWhyAmISeeingThis={true}
									whyAmISeeingThisModalContent={whyAmISeeingThisContent}
									spacing="compact"
								/>
								<CloseButton onClick={onClose} />
							</Inline>
						</Flex>
						<Box xcss={styles.children}>{children}</Box>
					</Stack>
				</Flex>
				{bottomLayer}
			</Box>
		</Box>
	);
};
